import { useState } from 'react';
import { Link } from 'react-router-dom';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  BILLING_MODE_LABELS,
  PERIODICITY_LABELS,
  SUBSCRIPTION_STATUS_LABELS,
  useDueSubscriptions,
  useGenerateSubscriptionInvoice,
  useSetSubscriptionStatus,
  useSubscriptions,
} from '../lib/subscriptions-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * ABONELİK SÖZLEŞMELERİ.
 *
 * ⚠⚠ BU EKRAN FATURA DÜZENLEMEZ ve düzenlemediğini SÖYLER. "Taslak üret"
 * düğmesi bir TASLAK açar; düzenleme (numara tahsisi, dondurma, Peppol'e
 * gönderim) geri dönüşsüzdür, fatura ekranından ve insan onayıyla yapılır.
 * Otomatik düzenleyen bir akış, yanlış bir fiyatı ya da bitmiş bir sözleşmeyi
 * sessizce yasal belgeye çevirirdi.
 */
export function SubscriptionsPage() {
  const due = useDueSubscriptions();
  const list = useSubscriptions();
  const uret = useGenerateSubscriptionInvoice();
  const durum = useSetSubscriptionStatus();
  const [hata, setHata] = useState<string | null>(null);
  const [uretilen, setUretilen] = useState<{ id: string; warnings: string[] } | null>(null);

  return (
    <>
      <PageHeader
        title="Abonelikler"
        description="Dönemsel sözleşmeler ve onlardan üretilen TASLAK faturalar. Üretmek, düzenlemek değildir."
      />

      <Card className="mt-4">
        <CardHeader
          title="Faturalanmayı bekleyen dönemler"
          description="Sondan faturalanan abonelikte dönem bitmeden, önden faturalanan abonelikte dönem başlamadan üretilmez."
        />
        {due.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : due.isError ? (
          <ErrorState message={errorMessage(due.error)} />
        ) : (due.data ?? []).length === 0 ? (
          <EmptyState
            title="Bekleyen dönem yok"
            description="Yürürlükteki aboneliklerin sıradaki dönemleri henüz faturalanabilir durumda değil."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Sözleşme</Th>
                <Th>Dönem</Th>
                <Th>Fatura son günü</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(due.data ?? []).map((d) => (
                <tr key={d.subscription.id}>
                  <Td>
                    <span className="text-sm font-medium">{d.subscription.name}</span>
                    <br />
                    <span className="text-xs text-[var(--c-ink-soft)]">
                      {PERIODICITY_LABELS[d.subscription.periodicity]} ·{' '}
                      {BILLING_MODE_LABELS[d.subscription.billingMode]}
                    </span>
                    {/*
                      ⚠ UYARILAR HİÇBİR ŞEYİ ENGELLEMEZ ve hiçbir yere DONMAZ.
                      Bunlar hukuki nitelemeler değil, operatöre "buna bak"
                      diyen notlardır.
                    */}
                    {d.warnings.map((w, i) => (
                      <p key={i} className="mt-1 text-xs text-[var(--c-warn)]">
                        ⚠ {w}
                      </p>
                    ))}
                  </Td>
                  <Td className="text-xs">
                    {date(d.period.start)} – {date(d.period.end)}
                  </Td>
                  {/*
                    ⚠ SON GÜN KB n° 1 md. 4 §3'ten: dönemin BİTİMİNİ izleyen
                    ayın 15'i. Abonelikler için genel kuraldan AYRI bir hüküm.
                  */}
                  <Td className="text-xs">
                    <strong>{date(d.invoiceDeadline)}</strong>
                  </Td>
                  <Td>
                    <Button
                      variant="secondary"
                      disabled={uret.isPending}
                      onClick={() => {
                        setHata(null);
                        setUretilen(null);
                        uret.mutate(d.subscription.id, {
                          onSuccess: (r) =>
                            setUretilen({ id: r.run.salesInvoiceId, warnings: r.warnings }),
                          onError: (e: unknown) => setHata(errorMessage(e)),
                        });
                      }}
                    >
                      Taslak üret
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {uretilen && (
          <div className="border-t border-[var(--c-border)] px-4 py-3">
            <p className="text-sm">
              Taslak üretildi.{' '}
              <Link className="underline" to={`/invoices/${uretilen.id}`}>
                Faturayı aç
              </Link>{' '}
              — <strong>henüz düzenlenmedi</strong>; numara tahsis edilmedi ve hiçbir şey geri
              dönüşsüz olmadı.
            </p>
          </div>
        )}
        {hata && <p className="px-4 pb-3 text-xs text-[var(--c-danger)]">{hata}</p>}
      </Card>

      <Card className="mt-4">
        <CardHeader title="Sözleşmeler" />
        {list.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : (list.data ?? []).length === 0 ? (
          <EmptyState title="Henüz abonelik yok" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ad</Th>
                <Th>Dönem</Th>
                <Th>Sıradaki dönem</Th>
                <Th align="right">Tutar</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((s) => (
                <tr key={s.id}>
                  <Td>
                    <span className="text-sm">{s.name}</span>
                    <br />
                    <span className="text-xs text-[var(--c-ink-soft)]">
                      {date(s.startDate)}
                      {s.endDate ? ` – ${date(s.endDate)}` : ' – süresiz'}
                    </span>
                  </Td>
                  <Td className="text-xs">
                    {PERIODICITY_LABELS[s.periodicity]}
                    <br />
                    <span className="text-[var(--c-ink-soft)]">
                      {BILLING_MODE_LABELS[s.billingMode]}
                    </span>
                  </Td>
                  <Td className="text-xs">{date(s.nextPeriodStart)}</Td>
                  <Td align="right">{money(s.periodAmountExclVat)}</Td>
                  <Td>
                    <Badge
                      tone={
                        s.status === 'ACTIVE' ? 'ok' : s.status === 'PAUSED' ? 'warn' : 'neutral'
                      }
                    >
                      {SUBSCRIPTION_STATUS_LABELS[s.status]}
                    </Badge>
                  </Td>
                  <Td>
                    {/*
                      ⚠ "SİL" YOK. Sözleşme silinmez, BİTİRİLİR: silinseydi
                      ondan üretilmiş faturaların dayanağı kaybolurdu. Ve
                      bitmiş sözleşme yeniden AÇILMAZ — aradaki dönemlerin
                      faturalanıp faturalanmadığı belirsiz kalırdı.
                    */}
                    {s.status !== 'ENDED' && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          disabled={durum.isPending}
                          onClick={() => {
                            setHata(null);
                            durum.mutate(
                              { id: s.id, status: s.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' },
                              { onError: (e: unknown) => setHata(errorMessage(e)) },
                            );
                          }}
                        >
                          {s.status === 'ACTIVE' ? 'Duraklat' : 'Sürdür'}
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={durum.isPending}
                          onClick={() => {
                            setHata(null);
                            durum.mutate(
                              { id: s.id, status: 'ENDED' },
                              { onError: (e: unknown) => setHata(errorMessage(e)) },
                            );
                          }}
                        >
                          Bitir
                        </Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
